import { type CentraMarketCode } from "@/data/supported-markets"
import { getMarketCodeFromAppLocale } from "@/i18n/apps"
import { localeToFieldName } from "@/src/utils/string/localeToFieldName"

export type VisibilityAwareItem = Partial<Record<CentraMarketCode, boolean>> & {
    visibility?: Record<string, boolean | undefined> | null
}

/** Merges coalesced market flags with the unified `visibility` object for `isContentVisible`. */
export const mergePageVisibility = (
    visibilityByMarket?: Partial<Record<CentraMarketCode, boolean>> | null,
    visibility?: VisibilityAwareItem["visibility"] | null
): VisibilityAwareItem | undefined => {
    if (!visibilityByMarket && !visibility) return undefined
    return { ...visibilityByMarket, visibility }
}

/** Page-level gate: visible when no visibility data exists, otherwise defers to `isContentVisible`. */
export const isPageVisibleForLocale = (
    locale: string | null | undefined,
    visibilityByMarket?: Partial<Record<CentraMarketCode, boolean>> | null,
    visibility?: VisibilityAwareItem["visibility"] | null
): boolean => {
    const merged = mergePageVisibility(visibilityByMarket, visibility)
    if (!merged) return true
    return isContentVisible(merged, locale)
}

/**
 * Visible-first content visibility check.
 *
 * Content is hidden only when a toggle is *explicitly* `false`:
 * - market layer: `item.visibility[<marketCode>] ?? item[<marketCode>]`
 *   (unified `visibility` object first, deprecated root-level market boolean
 *   as fallback; market is derived from the locale)
 * - locale layer: for every locale, bare or country-suffixed (e.g. `en` ->
 *   `item.visibility[localeEn]`, `en-se` -> `item.visibility[localeEnSe]`)
 *
 * `undefined`/`true` always resolve to visible, so content authored before the
 * toggles existed (no `locale*` field) stays visible.
 */
export const isContentVisible = (
    item: VisibilityAwareItem | null | undefined,
    locale: string | null | undefined
): boolean => {
    if (!item) return false
    if (!locale) return true

    const marketCode = getMarketCodeFromAppLocale(locale)
    if ((item.visibility?.[marketCode] ?? item[marketCode]) === false) return false

    const localeField = localeToFieldName(locale)
    if (item.visibility?.[localeField] === false) return false

    return true
}
